/**
 * Recording time and place in a converted video (#115, 1.158.0).
 *
 * `mp4-muxer` writes a new file: its creation time is the moment of the
 * conversion (`Date.now()`), and the place the iPhone kept in `udta` is gone.
 * Whoever passed the video on or imported it into a photo library got the
 * day of the upload and no place. The app itself never depended on it: time
 * and place are read from the original before converting and go to the
 * server as headers.
 *
 * **Both values come from the recording and from nowhere else.** The time is
 * the one the camera wrote (with its zone, or `mvhd` in UTC), the place the
 * ISO 6709 text as it stood in the original, byte for byte. A zone computed
 * from the trip's zone course would look measured in the file and be wrong
 * after a change of the course; the same decision as for photos
 * (`baueExif()` only writes a zone it has).
 *
 * **A failure costs the stamp, never the video.** Anything this code does not
 * understand returns `null`, and the converted file goes out as it was.
 */

/** Seconds between 1904-01-01 (MP4) and 1970-01-01 (Unix). */
const EPOCH_1904 = 2_082_844_800

/** What goes into the file. `null` leaves the value as the muxer wrote it. */
export interface RecordingStamp {
  /** Seconds since 1904-01-01 UTC, as MP4 counts. */
  created: number | null
  /** ISO 6709 as the recording wrote it, e.g. `+63.4269+010.3951+012.000/`. */
  iso6709: string | null
  /**
   * The place text as it stood in the recording, when `iso6709` is not that
   * text but written from its numbers -- for the diagnosis line (1.160.0).
   */
  placeRaw?: string
}

/** A moment (ISO 8601 with zone) in MP4 seconds. */
export function secondsSince1904(instant: string): number | null {
  const ms = Date.parse(instant)
  if (!Number.isFinite(ms)) return null
  const seconds = Math.floor(ms / 1000) + EPOCH_1904
  return seconds > 0 ? seconds : null
}

/** MP4 seconds as an ISO moment, for the diagnosis line. */
export function instantOf1904(seconds: number): string {
  return new Date((seconds - EPOCH_1904) * 1000).toISOString()
}

/**
 * Only what a camera writes: two signed coordinates, an optional altitude,
 * a closing slash. Anything else stays out of the file -- this text ends up
 * in other people's photo libraries.
 */
export function plausibleIso6709(text: string): string | null {
  const trimmed = text.trim()
  if (trimmed.length > 64) return null
  if (!/^[+-]\d{1,2}(\.\d+)?[+-]\d{1,3}(\.\d+)?([+-]\d+(\.\d+)?)?\/?$/.test(trimmed)) return null
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`
}

/**
 * ISO 6709 written from a latitude and longitude (1.160.0).
 *
 * For a recording whose place text the map reads but `plausibleIso6709()`
 * does not take. From use: a video with the pin in the chat came out of the
 * conversion without any place; the pin reads only the two numbers at the
 * start, the stamp asked for the whole text. The numbers are the recording's,
 * the form is ours -- signs and digits, nothing else goes into the file.
 */
export function iso6709Of(lat: number, lon: number): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
  const part = (value: number, width: number) =>
    `${value < 0 ? '-' : '+'}${Math.abs(value).toFixed(5).padStart(width + 6, '0')}`
  return `${part(lat, 2)}${part(lon, 3)}/`
}

/**
 * The stamp of a route film (1.160.0): the start of the day, time and place,
 * as the server stores them at the video (`attachPicture()` in
 * `DayCardsController`). From use, asked and decided: the film carried the
 * pin in the chat and nothing in the Photos app. The film is no recording,
 * so its numbers come from the day the card shows, not from a camera.
 */
export function routeFilmStamp(start: [number, number] | null | undefined, startedAt: string | null): RecordingStamp {
  return {
    created: startedAt === null ? null : secondsSince1904(startedAt),
    iso6709: start === null || start === undefined ? null : iso6709Of(start[0], start[1]),
  }
}

interface Box {
  type: string
  start: number
  header: number
  size: number
}

function typeAt(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3),
  )
}

/**
 * The boxes between `from` and `to`, one level deep. `null` for anything
 * that does not add up: a box that runs past its parent is a file this code
 * does not understand, and guessing there would break the video.
 */
function boxesIn(view: DataView, from: number, to: number): Box[] | null {
  const boxes: Box[] = []
  let offset = from
  while (offset < to) {
    if (offset + 8 > to) return null
    let size = view.getUint32(offset)
    let header = 8
    if (size === 1) {
      if (offset + 16 > to) return null
      size = view.getUint32(offset + 8) * 2 ** 32 + view.getUint32(offset + 12)
      header = 16
    } else if (size === 0) {
      size = to - offset
    }
    if (size < header || offset + size > to) return null
    boxes.push({ type: typeAt(view, offset + 4), start: offset, header, size })
    offset += size
  }
  return boxes
}

function child(view: DataView, parent: Box, type: string): Box | null {
  return boxesIn(view, parent.start + parent.header, parent.start + parent.size)?.find((b) => b.type === type) ?? null
}

/**
 * The creation time of a full box with the layout of `mvhd`, `tkhd` and
 * `mdhd`: version and flags, then creation and modification time, 32 bits in
 * version 0 and 64 in version 1.
 */
function timeOf(view: DataView, box: Box): number | null {
  const body = box.start + box.header
  if (box.size < box.header + 4 + 16) return null
  const version = view.getUint8(body)
  if (version === 1) return view.getUint32(body + 4) * 2 ** 32 + view.getUint32(body + 8)
  if (version === 0) return view.getUint32(body + 4)
  return null
}

/** Whether this box can carry the time: version 1, or version 0 below 2^32. */
function canHoldTime(view: DataView, box: Box, seconds: number): boolean {
  if (box.size < box.header + 4 + 16) return false
  const version = view.getUint8(box.start + box.header)
  return version === 1 || (version === 0 && seconds < 2 ** 32)
}

/** Write creation and modification time; asked with `canHoldTime()` first. */
function setTime(view: DataView, box: Box, seconds: number): void {
  const body = box.start + box.header
  if (view.getUint8(body) === 1) {
    for (const at of [body + 4, body + 12]) {
      view.setUint32(at, Math.floor(seconds / 2 ** 32))
      view.setUint32(at + 4, seconds % 2 ** 32)
    }
  } else {
    view.setUint32(body + 4, seconds)
    view.setUint32(body + 8, seconds)
  }
}

/**
 * The creation time in a `moov` body (as `holeMoov()` in `videoOrt.ts`
 * returns it: without its own header). `null` for 0, which is "unknown".
 */
export function createdInMoov(moovBody: Uint8Array): number | null {
  const view = new DataView(moovBody.buffer, moovBody.byteOffset, moovBody.byteLength)
  const mvhd = boxesIn(view, 0, moovBody.byteLength)?.find((b) => b.type === 'mvhd')
  if (mvhd === undefined) return null
  const seconds = timeOf(view, mvhd)
  return seconds === null || seconds === 0 ? null : seconds
}

/** `udta` with one `©xyz`, laid out as the iPhone writes it. */
function udtaWith(iso6709: string): Uint8Array {
  const text = new TextEncoder().encode(iso6709)
  const xyzSize = 8 + 4 + text.byteLength
  const out = new Uint8Array(8 + xyzSize)
  const view = new DataView(out.buffer)
  view.setUint32(0, out.byteLength)
  out.set([0x75, 0x64, 0x74, 0x61], 4) // udta
  view.setUint32(8, xyzSize)
  out.set([0xa9, 0x78, 0x79, 0x7a], 12) // ©xyz
  view.setUint16(16, text.byteLength)
  // Language code of "und", packed ISO 639-2/T -- what Apple writes here.
  view.setUint16(18, 0x55c4)
  out.set(text, 20)
  return out
}

/**
 * Apple's place key in `moov/meta`, laid out as an iPhone writes it (1.160.0):
 * `hdlr` with handler `mdta`, `keys` with the one key, `ilst` with its value
 * as UTF-8. From use: a converted video kept no place in the Photos app. The
 * iPhone writes its place here, and `©xyz` in `udta` alone is a form that
 * other programs read; both go in, so either reader finds it.
 *
 * In QuickTime this `meta` is not a full box (no version and flags), unlike
 * the one in ISO files -- the layout `appleSchluessel()` in `videoOrt.ts`
 * reads.
 */
const PLACE_KEY = 'com.apple.quicktime.location.ISO6709'

function metaWith(iso6709: string): Uint8Array {
  const encoder = new TextEncoder()
  const key = encoder.encode(PLACE_KEY)
  const value = encoder.encode(iso6709)
  const hdlrSize = 8 + 4 + 4 + 4 + 12 + 1
  const keysSize = 8 + 4 + 4 + 8 + key.byteLength
  const dataSize = 8 + 4 + 4 + value.byteLength
  const itemSize = 8 + dataSize
  const ilstSize = 8 + itemSize
  const out = new Uint8Array(8 + hdlrSize + keysSize + ilstSize)
  const view = new DataView(out.buffer)
  let at = 0
  const box = (size: number, type: string) => {
    view.setUint32(at, size)
    for (let i = 0; i < 4; i++) out[at + 4 + i] = type.charCodeAt(i)
    at += 8
  }
  box(out.byteLength, 'meta')
  box(hdlrSize, 'hdlr')
  at += 4 + 4 // version and flags, pre-defined
  for (let i = 0; i < 4; i++) out[at + i] = 'mdta'.charCodeAt(i)
  at += 4 + 12 + 1 // handler, reserved, empty name
  box(keysSize, 'keys')
  at += 4 // version and flags
  view.setUint32(at, 1)
  at += 4
  view.setUint32(at, 8 + key.byteLength)
  for (let i = 0; i < 4; i++) out[at + 4 + i] = 'mdta'.charCodeAt(i)
  out.set(key, at + 8)
  at += 8 + key.byteLength
  box(ilstSize, 'ilst')
  view.setUint32(at, itemSize) // the item is named after its key's index, 1
  view.setUint32(at + 4, 1)
  at += 8
  box(dataSize, 'data')
  view.setUint32(at, 1) // type: UTF-8
  view.setUint32(at + 4, 0) // locale
  out.set(value, at + 8)
  return out
}

/**
 * The finished file with recording time and place, or `null` if nothing was
 * written or the layout is not one this code knows.
 *
 * **The place makes `moov` longer**, and with `fastStart: 'in-memory'` it
 * stands in front of the picture data. Every chunk offset in `stco`/`co64`
 * then points the length of the new `udta` too early, and a player reads
 * garbage. They are moved by exactly that length, and only those that point
 * behind `moov`.
 */
export function stampMp4(file: Uint8Array, stamp: RecordingStamp): Uint8Array<ArrayBuffer> | null {
  if (stamp.created === null && stamp.iso6709 === null) return null
  const fileView = new DataView(file.buffer, file.byteOffset, file.byteLength)
  const top = boxesIn(fileView, 0, file.byteLength)
  const moovBox = top?.find((b) => b.type === 'moov')
  if (top === null || top === undefined || moovBox === undefined || moovBox.header !== 8) return null

  // Work on a copy of `moov`; the picture data is never touched.
  const moov = file.slice(moovBox.start, moovBox.start + moovBox.size)
  const view = new DataView(moov.buffer)
  const root: Box = { type: 'moov', start: 0, header: 8, size: moov.byteLength }
  const children = boxesIn(view, 8, moov.byteLength)
  if (children === null) return null

  let timed = false
  if (stamp.created !== null) {
    const timeBoxes: Box[] = []
    const mvhd = child(view, root, 'mvhd')
    if (mvhd !== null) timeBoxes.push(mvhd)
    for (const trak of children.filter((b) => b.type === 'trak')) {
      const tkhd = child(view, trak, 'tkhd')
      const mdia = child(view, trak, 'mdia')
      const mdhd = mdia === null ? null : child(view, mdia, 'mdhd')
      if (tkhd !== null) timeBoxes.push(tkhd)
      if (mdhd !== null) timeBoxes.push(mdhd)
    }
    // All or none: half a stamp leaves the boxes disagreeing with each other.
    const seconds = stamp.created
    timed = timeBoxes.length > 0 && timeBoxes.every((box) => canHoldTime(view, box, seconds))
    if (timed) for (const box of timeBoxes) setTime(view, box, seconds)
  }

  // A `udta` or `meta` already there is not ours (the muxer writes neither);
  // the place is then left out rather than merged into a box this code did
  // not build.
  const place = stamp.iso6709 === null || children.some((b) => b.type === 'udta' || b.type === 'meta')
    ? null
    : stamp.iso6709
  if (place === null && !timed) return null
  const parts = place === null ? [] : [udtaWith(place), metaWith(place)]
  const joined = new Uint8Array(parts.reduce((sum, part) => sum + part.byteLength, 0))
  let offset = 0
  for (const part of parts) {
    joined.set(part, offset)
    offset += part.byteLength
  }
  const delta = joined.byteLength

  if (delta > 0) {
    // Only offsets behind `moov` move; with `moov` at the end nothing does.
    const moovEnd = moovBox.start + moovBox.size
    const offsets: { box: Box; wide: boolean }[] = []
    for (const trak of children.filter((b) => b.type === 'trak')) {
      const mdia = child(view, trak, 'mdia')
      const minf = mdia === null ? null : child(view, mdia, 'minf')
      const stbl = minf === null ? null : child(view, minf, 'stbl')
      if (stbl === null) return null
      const stco = child(view, stbl, 'stco')
      const co64 = child(view, stbl, 'co64')
      if (stco === null && co64 === null) return null
      if (stco !== null) offsets.push({ box: stco, wide: false })
      if (co64 !== null) offsets.push({ box: co64, wide: true })
    }
    for (const { box, wide } of offsets) {
      const body = box.start + box.header
      const count = view.getUint32(body + 4)
      const width = wide ? 8 : 4
      if (box.header + 8 + count * width > box.size) return null
      for (let i = 0; i < count; i++) {
        const at = body + 8 + i * width
        const old = wide ? view.getUint32(at) * 2 ** 32 + view.getUint32(at + 4) : view.getUint32(at)
        if (old < moovEnd) continue
        const moved = old + delta
        if (wide) {
          view.setUint32(at, Math.floor(moved / 2 ** 32))
          view.setUint32(at + 4, moved % 2 ** 32)
        } else {
          // A 32-bit table that would overflow is a file this code leaves alone.
          if (moved >= 2 ** 32) return null
          view.setUint32(at, moved)
        }
      }
    }
    view.setUint32(0, moov.byteLength + delta)
  }

  const out = new Uint8Array(file.byteLength + delta)
  out.set(file.subarray(0, moovBox.start), 0)
  out.set(moov, moovBox.start)
  out.set(joined, moovBox.start + moov.byteLength)
  out.set(file.subarray(moovBox.start + moovBox.size), moovBox.start + moov.byteLength + delta)
  return out
}
